/**
 * UpdatesModal — shows update announcements on first visit.
 */
import { useState, useEffect, type FC } from 'react';
import { Icon } from './Icon';
import type { UpdateItem } from '../services/dataService';

const LAST_SEEN_KEY = 'witehik_last_seen_update';

interface Props {
  updates: UpdateItem[];
  forceOpen?: boolean;
  onClose?: () => void;
}

export const UpdatesModal: FC<Props> = ({ updates, forceOpen, onClose }) => {
  const [visible, setVisible] = useState(false);
  const [unseenUpdates, setUnseenUpdates] = useState<UpdateItem[]>([]);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    if (forceOpen) {
      setShowAll(true);
      setUnseenUpdates(updates);
      setVisible(true);
      return;
    }
    setShowAll(false);
    if (!updates || updates.length === 0) return;

    const lastSeen = localStorage.getItem(LAST_SEEN_KEY);
    const lastSeenTime = lastSeen ? parseInt(lastSeen, 10) : 0;

    const unseen = updates.filter((u) => u.created_at > lastSeenTime);
    if (unseen.length > 0) {
      setUnseenUpdates(unseen);
      setVisible(true);
    }
  }, [updates, forceOpen]);

  const handleClose = () => {
    setVisible(false);
    // Mark all updates as seen
    if (updates.length > 0) {
      const maxTime = Math.max(...updates.map((u) => u.created_at));
      localStorage.setItem(LAST_SEEN_KEY, String(maxTime));
    }
    onClose?.();
  };

  if (!visible || unseenUpdates.length === 0) {
    // When force-opened but no updates exist, show empty state
    if (forceOpen && visible) {
      return (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 backdrop-blur-sm"
          onClick={handleClose}
        >
          <div
            className="bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl w-[90vw] max-w-lg p-8 text-center"
            onClick={(e) => e.stopPropagation()}
          >
            <Icon name="bell-off" className="w-10 h-10 text-slate-500 mx-auto mb-3" />
            <p className="text-slate-400 text-sm">Обновлений пока нет</p>
            <button
              onClick={handleClose}
              className="mt-5 px-5 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl text-sm transition-colors"
            >
              Закрыть
            </button>
          </div>
        </div>
      );
    }
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={handleClose}
    >
      <div
        className="bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl w-[90vw] max-w-lg max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700/50">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
              <Icon name="sparkles" className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">Обновления</h2>
              <p className="text-xs text-slate-400">
                {showAll
                  ? `Всего: ${unseenUpdates.length}`
                  : unseenUpdates.length === 1
                    ? '1 новое обновление'
                    : `${unseenUpdates.length} новых обновлений`}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
          >
            <Icon name="x" className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4 custom-scroll">
          {unseenUpdates.map((upd) => (
            <div
              key={upd.id}
              className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4 space-y-2"
            >
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-white">{upd.title}</h3>
                <span className="text-[10px] bg-blue-500/20 text-blue-300 px-2 py-0.5 rounded-full font-medium">
                  v{upd.version}
                </span>
              </div>
              <p className="text-xs text-slate-300 leading-relaxed whitespace-pre-wrap">
                {upd.body}
              </p>
              <div className="text-[10px] text-slate-500">
                {new Date(upd.created_at).toLocaleDateString('ru-RU', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-700/50">
          <button
            onClick={handleClose}
            className="w-full px-5 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 rounded-xl text-sm font-semibold text-white transition-all shadow-lg"
          >
            Понятно
          </button>
        </div>
      </div>
    </div>
  );
};
